

import { Users, Phone, Mail, MapPin } from "lucide-react";

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface ClientInfoProps {
  client: Client | undefined;
}

export default function ClientInfo({ client }: ClientInfoProps) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-100 flex items-center justify-between">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-600" />
          Client Information
        </h3>
        {client?.phone && (
          <a
            href={`tel:${client.phone}`}
            className="px-3 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Call
          </a>
        )}
      </div>
      <div className="p-4">
        {client ? (
          <div className="space-y-4">
            {/* Client Name */}
            <div>
              <p className="text-lg font-bold text-stone-800">{client.name}</p>
              {client.email && (
                <p className="text-sm text-stone-500 mt-0.5">{client.email}</p>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-2">
              {client.phone && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-stone-50 border border-stone-100">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 uppercase tracking-wide">Phone</p>
                    <p className="text-sm font-medium text-stone-700">{client.phone}</p>
                  </div>
                </div>
              )}

              {client.email && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-stone-50 border border-stone-100">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 uppercase tracking-wide">Email</p>
                    <p className="text-sm font-medium text-stone-700 truncate">{client.email}</p>
                  </div>
                </div>
              )}

              {client.address && (
                <div className="flex items-start gap-3 p-2 rounded-lg bg-stone-50 border border-stone-100">
                  <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 uppercase tracking-wide">Address</p>
                    <p className="text-sm font-medium text-stone-700">{client.address}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <Users className="w-10 h-10 text-stone-200 mx-auto mb-2" />
            <p className="text-sm text-stone-400">No client information available</p>
          </div>
        )}
      </div>
    </div>
  );
}
